import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import GalleryCard from "../../shared/components/GalleryCard";
import FadeInImage from "../../shared/components/FadeInImage";

import * as API from '../../api';
import { Link, useParams } from "react-router-dom";

/**
 * Premium /gallery/:id detail page.
 *
 * Data flow unchanged from the legacy page — same API.Gallery(id) call.
 * Visual language matches Events/Detail: hero header, photo grid with a
 * full-screen lightbox (same pattern as EventGallery), video embeds,
 * description, and a related-albums carousel below (GalleryCard).
 */

function youtube_parser(url) {
  let regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  let match = url.match(regExp);
  return (match && match[7] && match[7].length === 11) ? match[7] : false;
}

function GalleryLightbox({ images, title }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const pics = (images || []).filter((p) => p && p.image);
  if (!pics.length) return null;

  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
  };
  const prev = () => setIndex((i) => (i - 1 + pics.length) % pics.length);
  const next = () => setIndex((i) => (i + 1) % pics.length);

  const single = pics.length === 1;
  const duo = pics.length === 2;

  return (
    <>
      <div className="relative">
        <div
          className={
            single
              ? 'overflow-hidden rounded-2xl'
              : 'grid h-[300px] grid-cols-1 gap-2 overflow-hidden rounded-2xl sm:h-[380px] sm:grid-cols-2 lg:h-[440px]'
          }
        >
          <button
            type="button"
            onClick={() => openAt(0)}
            className={`group relative overflow-hidden ${single ? '' : duo ? '' : 'sm:row-span-2'}`}
          >
            <FadeInImage
              src={pics[0].image}
              alt={title}
              eager
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/5" />
          </button>

          {!single &&
            pics.slice(1, duo ? 2 : 3).map((p, i) => (
              <button
                key={p.id ?? i}
                type="button"
                onClick={() => openAt(i + 1)}
                className="group relative hidden overflow-hidden sm:block"
              >
                <FadeInImage
                  src={p.image}
                  alt={title}
                  className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/5" />
              </button>
            ))}
        </div>

        {pics.length > 1 && (
          <button
            type="button"
            onClick={() => openAt(0)}
            className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-ink shadow-card backdrop-blur transition-transform hover:scale-105 rtl:right-auto rtl:left-4"
          >
            <Squares2X2Icon className="h-4 w-4" />
            {t('galleries.showAllPhotos')} · {pics.length}
          </button>
        )}
      </div>

      <Transition show={open} as={Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-[60]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/90" />
          </Transition.Child>

          <div className="fixed inset-0 flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 text-white/90">
              <span className="text-sm font-medium">
                {index + 1} / {pics.length}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="relative flex flex-1 items-center justify-center px-4 pb-8">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <ChevronLeftIcon className="h-6 w-6 rtl:rotate-180" />
              </button>

              <img
                src={pics[index].image}
                alt={title}
                className="max-h-full max-w-full rounded-xl object-contain"
              />

              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <ChevronRightIcon className="h-6 w-6 rtl:rotate-180" />
              </button>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-container flex-col items-center justify-center px-4 py-20 text-center">
      <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-brand-50">
        <PhotoIcon className="h-11 w-11 text-brand-300" />
        <span className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent-500 text-base font-bold text-brand-900">
          ?
        </span>
      </div>
      <h1 className="mt-6 font-display text-2xl font-bold text-ink sm:text-3xl">
        {t('galleries.notFoundTitle')}
      </h1>
      <p className="mt-2.5 max-w-md text-sm text-ink-muted sm:text-base">
        {t('galleries.notFoundBody')}
      </p>
      <Link
        to="/galleries"
        className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-600"
      >
        {t('galleries.backToGalleries')}
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
        </svg>
      </Link>
    </div>
  );
}

function Details() {
  const { t } = useTranslation();
  const [ gallery        , setGallery        ] = React.useState({});
  const [ loadingGallery , setLoadingGallery ] = React.useState(false);
  const { id } = useParams();

  React.useEffect(()=>{
    setLoadingGallery(true);
    API.Gallery( id )
      .then((res)=>{
        setGallery( res.data );
      })
      .catch((err)=>{
        //console.log(err);
      })
      .then(()=>{
        setLoadingGallery(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[0,id]);

  const hasGallery = Object.keys(gallery).length > 0;

  return (
    <div className="bg-surface-muted">
      {/* ---------- Page header ---------- */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto flex w-full max-w-container flex-col items-center px-4 py-10 text-center sm:px-6 sm:py-14 lg:px-8">
          {loadingGallery ? (
            <div className="h-9 w-64 max-w-full animate-pulse rounded-lg bg-slate-100 sm:h-11 sm:w-96" />
          ) : (
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-[40px] sm:leading-tight">
              {gallery.title || ''}
            </h1>
          )}
          {!loadingGallery && gallery.categorie?.name ? (
            <span className="mt-3 inline-flex items-center rounded-full bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-600">
              {gallery.categorie.name}
            </span>
          ) : null}
        </div>
      </header>

      {/* ---------- Body ---------- */}
      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        {loadingGallery ? (
          <div className="grid h-[300px] grid-cols-1 gap-2 overflow-hidden rounded-2xl sm:h-[380px] sm:grid-cols-2 lg:h-[440px]">
            <div className="animate-pulse bg-slate-100 sm:row-span-2" />
            <div className="hidden animate-pulse bg-slate-100 sm:block" />
            <div className="hidden animate-pulse bg-slate-100 sm:block" />
          </div>
        ) : hasGallery ? (
          <>
            {parseInt(gallery.images_count) ? (
              <GalleryLightbox images={gallery.images} title={gallery.title} />
            ) : null}

            {parseInt(gallery.vedios_count) ? (
              <section className="mt-8">
                <h2 className="mb-4 font-display text-xl font-semibold text-ink">
                  {t('galleries.videos')}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {gallery.vedios.map((video, index) => {
                    const yid = youtube_parser(video.url);
                    if (!yid) return null;
                    return (
                      <div
                        key={video.id ?? index}
                        className="aspect-video overflow-hidden rounded-2xl shadow-card"
                      >
                        <iframe
                          src={`https://www.youtube.com/embed/${yid}`}
                          title={`video-${video.id ?? index}`}
                          className="h-full w-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="mt-8 border-t border-slate-100 pt-7">
              <div
                className="prose prose-sm max-w-none text-ink-muted [&_a]:font-medium [&_a]:text-brand-500 [&_a:hover]:underline [&_h2]:text-ink [&_h3]:text-ink [&_img]:rounded-xl [&_p]:my-2 [&_strong]:text-ink"
                dangerouslySetInnerHTML={{ __html: gallery.description }}
              />
            </section>
          </>
        ) : (
          <NotFound />
        )}
      </div>

      <div className="border-t border-slate-100">
        <GalleryCard titleSection={t('galleries.otherAlbums')} />
      </div>
    </div>
  );
}

export default Details;
